import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ExternalLink, Loader2, PanelRightClose } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function buildPreviewUrl(url) {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (BACKEND_URL && url.startsWith(BACKEND_URL)) return url;
  return `${BACKEND_URL}/api/preview/web?url=${encodeURIComponent(url)}`;
}

export default function LinkPreviewPanel({ url, title, onClose }) {
  const [loading, setLoading] = useState(true);
  const [width, setWidth] = useState(() => {
    if (typeof window === 'undefined') return 560;
    return Math.min(760, Math.max(420, Math.round(window.innerWidth * 0.42)));
  });
  const resizeState = useRef({ dragging: false, startX: 0, startWidth: 0 });

  useEffect(() => {
    setLoading(true);
  }, [url]);

  const startResize = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    resizeState.current = { dragging: true, startX: e.clientX, startWidth: width };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizeState.current.dragging) return;
      const nextWidth = resizeState.current.startWidth + (resizeState.current.startX - e.clientX);
      setWidth(Math.min(880, Math.max(360, nextWidth)));
    };
    const onUp = () => {
      if (!resizeState.current.dragging) return;
      resizeState.current.dragging = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  if (!url) return null;

  const previewUrl = buildPreviewUrl(url);

  const panel = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        pointerEvents: 'none',
      }}
    >
      <aside
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width,
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-base, #0d1117)',
          borderLeft: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
          boxShadow: '0 18px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div
          onMouseDown={startResize}
          title="Resize preview"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: 8,
            cursor: 'col-resize',
            background: 'transparent',
          }}
        />

        <div
          style={{
            padding: '14px 18px 12px',
            borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
            background: 'var(--bg-surface, #11161d)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--accent-primary, #2ecc5a)',
                marginBottom: 4,
                fontFamily: 'var(--font-ui)',
              }}
            >
              Preview
            </div>
            <div
              style={{
                fontSize: 15,
                lineHeight: 1.35,
                color: 'var(--text-primary, #f3f5f7)',
                fontWeight: 600,
                fontFamily: 'var(--font-ui)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={title || url}
            >
              {title || url}
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-muted, rgba(255,255,255,0.5))',
                marginTop: 3,
                fontFamily: "'JetBrains Mono', monospace",
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={url}
            >
              {url}
            </div>
          </div>

          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 10px',
              borderRadius: 10,
              border: '1px solid var(--border-default, rgba(255,255,255,0.12))',
              color: 'var(--text-secondary, #d1d5db)',
              textDecoration: 'none',
              fontSize: 12,
              fontFamily: 'var(--font-ui)',
              flexShrink: 0,
              background: 'var(--bg-elevated, rgba(255,255,255,0.03))',
            }}
            title="Open in a new tab"
          >
            <ExternalLink size={12} />
            Open
          </a>

          <button
            onClick={onClose}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 10,
              border: '1px solid var(--border-default, rgba(255,255,255,0.12))',
              background: 'var(--bg-elevated, rgba(255,255,255,0.03))',
              color: 'var(--text-secondary, #d1d5db)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
            title="Close preview"
          >
            <PanelRightClose size={15} />
          </button>
        </div>

        <div style={{ flex: 1, position: 'relative', background: '#fff' }}>
          {loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                background: 'var(--bg-base, #0d1117)',
              }}
            >
              <Loader2 size={22} className="animate-spin" style={{ color: 'var(--accent-primary, #2ecc5a)' }} />
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--text-muted, rgba(255,255,255,0.5))',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                Loading preview...
              </span>
            </div>
          )}

          <iframe
            key={previewUrl}
            src={previewUrl}
            title={title || 'Preview'}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            onLoad={() => setLoading(false)}
          />
        </div>
      </aside>
    </div>
  );

  return ReactDOM.createPortal(panel, document.body);
}
